import { z } from 'zod';

import { CivilDateDto } from './civilDate';
import { ProspectKind, ProspectStatus } from '../enums';

// A hand-entered prospect needs at least one way to be reached: an email,
// a phone number, or both.
export const CreateProspectDto = z
  .object({
    firstName: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit'),
    lastName: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit'),
    email: z.string().email('VALIDATION__InvalidEmail').optional(),
    phoneNumber: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(50, 'VALIDATION__Max50CharLimit')
      .optional(),
  })
  .refine((dto) => dto.email || dto.phoneNumber, {
    message: 'VALIDATION__EmailOrPhoneRequired',
    path: ['phoneNumber'],
  });

export type ApiCreateProspectDto = z.infer<typeof CreateProspectDto>;

export const UpdateProspectDto = z
  .object({
    firstName: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    lastName: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    email: z.string().email('VALIDATION__InvalidEmail').nullable().optional(),
    phoneNumber: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(50, 'VALIDATION__Max50CharLimit')
      .nullable()
      .optional(),
  })
  .strict()
  // One contact may be cleared, never both in the same payload.
  .refine((dto) => !(dto.email === null && dto.phoneNumber === null), {
    message: 'VALIDATION__EmailOrPhoneRequired',
    path: ['phoneNumber'],
  });

export type ApiUpdateProspectDto = z.infer<typeof UpdateProspectDto>;

export const ListProspectsQueryDto = z.object({
  search: z.string().trim().max(255).optional(),
  // 1-indexed page number, not a raw SQL offset — see `listQueryOptions`
  // in `ProspectService`, which converts via `(offset - 1) * limit`.
  offset: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  orderBy: z.enum(['name', 'createdAt']).optional().default('createdAt'),
  orderDirection: z.enum(['ASC', 'DESC']).optional().default('DESC'),
  // Unset = All (prospects + former clients), matching the FE's "All" filter
  // option.
  kind: z.enum([ProspectKind.PROSPECT, ProspectKind.FORMER_CLIENT]).optional(),
});

export type ApiListProspectsQueryDto = z.infer<typeof ListProspectsQueryDto>;

export const ProspectStatusEnum = z.enum([
  ProspectStatus.ACTIVE,
  ProspectStatus.ARCHIVED,
]);

export type ProspectStatusDto = z.infer<typeof ProspectStatusEnum>;

// Restricted to the org's DAILY ticket templates (duration=1, DAYS) — see
// decision in the Hot Prospects plan: a prospect has no account, so it may
// only ever be sold a walk-in day pass, never a recurring membership. This
// DTO cannot express that (it doesn't see the catalog row); the server-side
// gate in `ProspectService.assignTicket` is the one place that enforces it.
export const AssignProspectTicketDto = z.object({
  membershipId: z.string().uuid(),
  startDate: CivilDateDto,
  discountPercent: z.number().min(0).max(100).optional().default(0),
  // Free amount the desk actually collected. Omitted = the catalog price
  // after `discountPercent`. 0 is allowed (gratis) but must be explained in
  // `notes` — the server enforces that.
  amount: z.number().min(0).optional(),
  paymentMethod: z.enum(['CASH', 'CARD', 'TRANSFER', 'OTHER']),
  notes: z.string().max(2000).optional(),
  // Day pass WITH a trainer (1.11): required exactly when the template
  // `includesTrainer`, refused otherwise — the server enforces both.
  trainerId: z.string().uuid().optional(),
});

export type ApiAssignProspectTicketDto = z.infer<
  typeof AssignProspectTicketDto
>;

// Storno of a prospect's day pass. The reason is mandatory: it is the only
// explanation the audit trail keeps of why a recorded payment was struck out.
export const VoidProspectTicketDto = z
  .object({
    reason: z.string().trim().min(1, 'VALIDATION__Required').max(2000),
  })
  .strict();

export type ApiVoidProspectTicketDto = z.infer<typeof VoidProspectTicketDto>;
